import { supabase } from '../supabase.ts';
import { parseTimeRange, toTimeRange } from './locations.ts';

/**
 * Delivery zones and their bookable windows.
 *
 * A zone is a polygon, a ZIP list, or both -- `delivery_zones_area_or_zips`
 * requires at least one, because a zone that matches nothing would silently
 * refuse every address. `find_delivery_zones` prefers the polygon and falls
 * back to the ZIPs, so drawing an area over a ZIP list narrows it.
 */

export interface ZoneRecord {
  id: string;
  name: string;
  active: boolean;
  color: string | null;
  priority: number;
  locationId: string | null;
  zipCodes: string[];
  deliveryFeeCents: number;
  deliveryMinimumCents: number;
  freeDeliveryOverCents: number | null;
  leadTimeMinutes: number;
  cutoffTime: string | null;
  taxJurisdictionId: string | null;
  /** True when a polygon is stored; the geometry itself comes from the RPC. */
  hasArea: boolean;
}

const ZONE_COLUMNS =
  'id, name, active, color, priority, location_id, zip_codes, delivery_fee_cents, delivery_minimum_cents, free_delivery_over_cents, lead_time_minutes, cutoff_time, tax_jurisdiction_id, area';

export async function listZones(accountId: string): Promise<ZoneRecord[]> {
  const { data, error } = await supabase
    .from('delivery_zones')
    .select(ZONE_COLUMNS)
    .eq('account_id', accountId)
    .order('priority', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    active: row.active,
    color: row.color,
    priority: row.priority,
    locationId: row.location_id,
    zipCodes: row.zip_codes ?? [],
    deliveryFeeCents: row.delivery_fee_cents,
    deliveryMinimumCents: row.delivery_minimum_cents,
    freeDeliveryOverCents: row.free_delivery_over_cents,
    leadTimeMinutes: row.lead_time_minutes,
    // `time` comes back as `20:00:00`; the input wants `20:00`.
    cutoffTime: row.cutoff_time ? String(row.cutoff_time).slice(0, 5) : null,
    taxJurisdictionId: row.tax_jurisdiction_id,
    hasArea: row.area !== null,
  }));
}

export interface PolygonGeometry {
  type: 'Polygon';
  coordinates: number[][][];
}

function isCoordinate(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1]) &&
    Math.abs(value[0]) <= 180 &&
    Math.abs(value[1]) <= 90
  );
}

/**
 * A drawn polygon as EWKT, which is what a `geography` column accepts from
 * PostgREST -- there is no GeoJSON encoder on that path, and building the
 * literal here keeps saving a zone one write rather than an insert plus an
 * RPC. Anything malformed becomes null, i.e. "no area", never an unchecked
 * SQL string.
 */
export function polygonToEwkt(polygon: PolygonGeometry | null): string | null {
  if (polygon?.type !== 'Polygon') return null;

  const rings: string[] = [];

  for (const ring of polygon.coordinates ?? []) {
    if (!Array.isArray(ring) || ring.length < 4) return null;

    const points: string[] = [];

    for (const point of ring) {
      if (!isCoordinate(point)) return null;

      points.push(`${point[0]} ${point[1]}`);
    }

    // PostGIS rejects an unclosed ring outright.
    if (points[0] !== points.at(-1)) points.push(points[0] as string);

    rings.push(`(${points.join(',')})`);
  }

  if (!rings.length) return null;

  return `SRID=4326;POLYGON(${rings.join(',')})`;
}

/** The stored polygon, as GeoJSON, through the RPC that decodes it. */
export async function getZoneArea(
  accountId: string,
  zoneId: string,
): Promise<PolygonGeometry | null> {
  const { data, error } = await supabase.rpc('delivery_zone_areas', {
    p_account_id: accountId,
    p_zone_id: zoneId,
  });

  if (error) throw error;

  const first = (data ?? [])[0];
  const geometry = first?.area as PolygonGeometry | null | undefined;

  return geometry?.type === 'Polygon' ? geometry : null;
}

export interface ZoneInput {
  name: string;
  active: boolean;
  color: string | null;
  priority: number;
  locationId: string | null;
  zipCodes: string[];
  deliveryFeeCents: number;
  deliveryMinimumCents: number;
  freeDeliveryOverCents: number | null;
  leadTimeMinutes: number;
  cutoffTime: string | null;
  taxJurisdictionId: string | null;
  /** Undefined leaves the stored polygon alone; null clears it. */
  area?: PolygonGeometry | null;
}

type ZoneRow = {
  name: string;
  active: boolean;
  color: string | null;
  priority: number;
  location_id: string | null;
  zip_codes: string[];
  delivery_fee_cents: number;
  delivery_minimum_cents: number;
  free_delivery_over_cents: number | null;
  lead_time_minutes: number;
  cutoff_time: string | null;
  tax_jurisdiction_id: string | null;
  /** EWKT; PostGIS casts it to geography on the way in. */
  area?: string | null;
};

function toRow(input: ZoneInput): ZoneRow {
  const row: ZoneRow = {
    name: input.name,
    active: input.active,
    color: input.color,
    priority: input.priority,
    location_id: input.locationId,
    zip_codes: input.zipCodes,
    delivery_fee_cents: input.deliveryFeeCents,
    delivery_minimum_cents: input.deliveryMinimumCents,
    free_delivery_over_cents: input.freeDeliveryOverCents,
    lead_time_minutes: input.leadTimeMinutes,
    cutoff_time: input.cutoffTime,
    tax_jurisdiction_id: input.taxJurisdictionId,
  };

  if (input.area !== undefined) row.area = polygonToEwkt(input.area);

  return row;
}

export async function createZone(accountId: string, input: ZoneInput): Promise<string> {
  const { data, error } = await supabase
    .from('delivery_zones')
    .insert({ account_id: accountId, ...toRow(input) })
    .select('id')
    .single();

  if (error) throw error;

  return data.id;
}

export async function updateZone(accountId: string, id: string, input: ZoneInput): Promise<void> {
  const { error } = await supabase
    .from('delivery_zones')
    .update(toRow(input))
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

export async function deleteZone(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('delivery_zones')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Windows                                                                     */
/* -------------------------------------------------------------------------- */

export interface WindowRow {
  id: number;
  zoneId: string;
  dayOfWeek: number;
  startsAt: string;
  endsAt: string;
  maxOrders: number | null;
  active: boolean;
}

export async function listWindows(accountId: string, zoneId: string): Promise<WindowRow[]> {
  const { data, error } = await supabase
    .from('delivery_windows')
    .select('id, zone_id, day_of_week, slot, max_orders, active')
    .eq('account_id', accountId)
    .eq('zone_id', zoneId)
    .order('day_of_week', { ascending: true });

  if (error) throw error;

  return (
    (data ?? []) as unknown as Array<{
      id: number;
      zone_id: string;
      day_of_week: number;
      slot: string;
      max_orders: number | null;
      active: boolean;
    }>
  ).flatMap((row) => {
    const parsed = parseTimeRange(String(row.slot));

    return parsed
      ? [
          {
            id: row.id,
            zoneId: row.zone_id,
            dayOfWeek: row.day_of_week,
            startsAt: parsed.opensAt,
            endsAt: parsed.closesAt,
            maxOrders: row.max_orders,
            active: row.active,
          },
        ]
      : [];
  });
}

export async function addWindow(
  accountId: string,
  input: {
    zoneId: string;
    dayOfWeek: number;
    startsAt: string;
    endsAt: string;
    maxOrders: number | null;
  },
): Promise<void> {
  const { error } = await supabase.from('delivery_windows').insert({
    account_id: accountId,
    zone_id: input.zoneId,
    day_of_week: input.dayOfWeek,
    slot: toTimeRange(input.startsAt, input.endsAt),
    max_orders: input.maxOrders,
  });

  if (error) throw error;
}

export async function deleteWindow(accountId: string, id: number): Promise<void> {
  const { error } = await supabase
    .from('delivery_windows')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/**
 * Copy one day's windows across the week.
 *
 * Every store that offers delivery windows offers roughly the same ones every
 * day; without this the operator types 21 rows by hand.
 */
export async function copyWindowsToWeek(
  accountId: string,
  zoneId: string,
  source: WindowRow[],
): Promise<void> {
  const rows = [0, 1, 2, 3, 4, 5, 6].flatMap((day) =>
    source.map((window) => ({
      account_id: accountId,
      zone_id: zoneId,
      day_of_week: day,
      slot: toTimeRange(window.startsAt, window.endsAt),
      max_orders: window.maxOrders,
    })),
  );

  // The exclusion constraint refuses a duplicate outright; ignoring conflicts
  // is what makes "apply to every day" idempotent.
  const { error } = await supabase
    .from('delivery_windows')
    .upsert(rows, { onConflict: 'account_id,zone_id,day_of_week,slot', ignoreDuplicates: true });

  if (error) throw error;
}
